// The trace-review pipeline's REPORTS for one conversation, read through the
// review API and laid on the lanes' turn axis as the chapters of a story.
//
// The pipeline judges a conversation window by window and writes one report
// per window. Every report carries a short summary the judge is forbidden from
// spending on its incidents (the schema says "never restates the incidents"),
// so on a clean report the summary can only say what the agent DID in that
// window. Read in window order, the summaries are a synopsis of the session at
// review grain: task prompt, sub-agent review, merge, build, rollout, verify.
// This module is the dashboard's read of that sequence, and the findings strip
// (traceReviewFindings.js) is its sibling: findings are the exceptions, the
// reports are the chapters they sit in.
//
// Two hops, both through the review API rather than the analytics projection,
// because the summary lives only in the Postgres report document -- the
// ClickHouse projection is header grain by design:
//   1. GET .../review-state: the PREFERRED report header per reviewed window,
//      so superseded re-evaluations do not appear as duplicate chapters.
//   2. GET .../conversation-review-reports/{id} per header: the summary, the
//      verdict, and the window's target occurrences (message ids).
// The second hop is a bounded fan-out (REPORTS_LIMIT newest windows, a few in
// flight), cached per conversation for the identity epoch.
//
// Placement is by MESSAGE ID: a report's target occurrences name the messages
// it reviewed, the loaded turns carry their message ids, and the window on the
// axis is the first through the last turn those ids land on. A report whose
// targets the loaded turns do not carry -- or whose manifest has aged out with
// its task -- stays in the list, ordered by completion, marked as not placed,
// and never drawn on the axis.
import { authedFetch, idForPath } from "./restClient.js";
import { endpointIdToUuid } from "./inventoryRows.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";
import { parseServerTimestamp } from "./traceUtils.js";
import { behaviourTone, contentTone } from "./traceReview.js";
import { adaptFinding, adaptReportHeader } from "./reviewClient.js";

// Newest windows fetched per conversation: the most the review-state read
// hands back (its MAX_PREFERRED_REPORTS). A long-lived session accrues one
// report per review trigger -- 145 on one staging session -- so past this the
// strip says "newest N shown" and the axis's older stretch carries no
// chapters; covering it needs the read to page or to select by message range.
export const REPORTS_LIMIT = 50;
// Report documents in flight at once. Each is one Postgres read behind the
// gateway; a wider fan-out for one trace open buys nothing.
export const REPORT_FETCH_CONCURRENCY = 6;

// Hyphenated uuid -> standard base64 of its 16 raw bytes (protobuf `bytes`
// over the JSON gateway). Null for anything that is not a uuid, which is how a
// harness or fixture session id opts out of the read.
export function uuidToBytesBase64(uuid) {
    const hex = String(uuid || "").replace(/-/g, "").toLowerCase();
    if (!/^[0-9a-f]{32}$/.test(hex)) return null;
    let bin = "";
    for (let i = 0; i < 32; i += 2) bin += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16));
    return btoa(bin);
}

export function reviewStatePath(sessionId) {
    const id = uuidToBytesBase64(sessionId);
    return id ? `/v1/conversation-traces/${idForPath(id)}/review-state` : null;
}

export function reviewReportPath(reportIdBase64) {
    return `/v1/conversation-review-reports/${idForPath(reportIdBase64)}`;
}

async function getJson(path, signal) {
    const res = await authedFetch(path, { method: "GET", signal });
    if (!res.ok) {
        const err = new Error(`GET ${path} ${res.status}: ${res.statusText}`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

// A fixed pool over the report fetches: `limit` workers each draining the same
// queue, so the fan-out never exceeds the pool no matter how many windows.
export async function runPooled(tasks, limit) {
    const out = new Array(tasks.length);
    let next = 0;
    const worker = async () => {
        for (;;) {
            const i = next++;
            if (i >= tasks.length) return;
            out[i] = await tasks[i]();
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
    return out;
}

const str = (v) => (v == null ? "" : String(v)).trim();
// "BEHAVIOUR_SEVERITY_HIGH" -> "high"; the unspecified and none rungs read as
// no rung at all, which is what the tone helpers treat as calm.
function rung(value, prefix) {
    const s = str(value).toUpperCase();
    const bare = s.startsWith(prefix) ? s.slice(prefix.length) : s;
    const lower = bare.toLowerCase();
    return !lower || lower === "unspecified" || lower === "none" ? null : lower;
}

// One fetched report as the cache holds it: everything the strip and the dock
// read, and nothing else. Message ids are canonical lowercase uuids so the join
// against the loaded turns is a string equality.
export function adaptReport(header, doc) {
    const h = header || doc?.header || {};
    const adaptedHeader = adaptReportHeader(h);
    const completedAt = parseServerTimestamp(h.completedAt);
    const messageOf = (occ) => str(endpointIdToUuid(str(occ?.messageId))).toLowerCase();
    const targets = Array.isArray(doc?.targetOccurrences) ? doc.targetOccurrences.map(messageOf) : [];
    const contexts = Array.isArray(doc?.contextOccurrences) ? doc.contextOccurrences.map(messageOf) : [];
    const ids = new Set(targets.filter(Boolean));
    return {
        id: str(h.reportId),
        reportId: adaptedHeader.reportId,
        completedAt: Number.isFinite(completedAt) ? completedAt : null,
        summary: str(doc?.summary),
        temporalNote: str(doc?.temporalNote),
        clean: !!doc?.clean,
        expired: !!doc?.documentExpired,
        partial: !!h.isPartial,
        findingCount: Number(h.findingCount) || 0,
        actionableFindingCount: Number(h.actionableFindingCount) || 0,
        worstBehaviour: rung(h.worstBehaviourSeverity, "BEHAVIOUR_SEVERITY_"),
        worstContent: rung(h.worstContentSensitivity, "CONTENT_SENSITIVITY_"),
        model: str(doc?.providerModelIdentifier) || str(h.modelConfigurationId),
        profileVersion: adaptedHeader.profileVersion,
        scope: adaptedHeader.scope,
        scopeProfileVersion: adaptedHeader.scopeProfileVersion,
        promptBundle: str(doc?.promptBundle),
        targetMessageIds: [...ids],
        // The window's items in the order the judge was shown them, one entry
        // per item, so its prose refs resolve: t1 is the first target, c1 the
        // first context item. The published lists leave out items whose
        // content was unavailable, so a report with coverage gaps cannot be
        // resolved by position.
        targetOrder: targets,
        contextOrder: contexts,
        coverageGapCount: Array.isArray(doc?.coverageGaps) ? doc.coverageGaps.length : 0,
        findings: (Array.isArray(doc?.findings) ? doc.findings : []).map((finding) => ({
            ...adaptFinding(finding),
            reportId: adaptedHeader.reportId,
        })),
    };
}

// -> { rows, truncated, enabled }. `enabled` false means the backend review
// path is off for this environment and the strip has nothing to say. Throws
// on a failed read (denied, unreachable); the caller decides how quiet to be.
export async function fetchTraceReviewReports({ sessionId, signal = null, limit = REPORTS_LIMIT } = {}) {
    const statePath = reviewStatePath(sessionId);
    if (!statePath) return { rows: [], truncated: false, enabled: false };
    const state = await getJson(statePath, signal);
    const enabled = state?.backendReviewEnabled !== false;
    const headers = (Array.isArray(state?.preferredReports) ? state.preferredReports : [])
        .filter((h) => str(h?.reportId))
        .sort((a, b) => (parseServerTimestamp(b.completedAt) || 0) - (parseServerTimestamp(a.completedAt) || 0));
    const page = headers.slice(0, limit);
    const docs = await runPooled(
        page.map((h) => () => getJson(reviewReportPath(str(h.reportId)), signal)),
        REPORT_FETCH_CONCURRENCY,
    );
    return {
        rows: page.map((h, i) => adaptReport(h, docs[i])),
        truncated: headers.length > page.length,
        enabled,
    };
}

// -- cache ------------------------------------------------------------------
// Per conversation, so re-opening a trace does not re-walk the report API.
// Cleared on identity change: the summaries are tenant content.
const CACHE = new Map();
export const REPORTS_CACHE_MAX = 32;
onAuthIdentityChange(() => CACHE.clear());

export function reportsCacheKey(sessionId) {
    return `${getAuthIdentityEpoch()}:${sessionId}`;
}
export function readReportsCache(sessionId) {
    return CACHE.get(reportsCacheKey(sessionId)) || null;
}
export function writeReportsCache(sessionId, result) {
    if (CACHE.size >= REPORTS_CACHE_MAX) {
        const oldest = CACHE.keys().next().value;
        if (oldest !== undefined) CACHE.delete(oldest);
    }
    CACHE.set(reportsCacheKey(sessionId), result);
}
// Harness and test seam: a pre-seeded answer renders through the cache-hit
// path with no backend.
export function seedTraceReviewReports(sessionId, rows, { truncated = false } = {}) {
    writeReportsCache(sessionId, { rows: Array.isArray(rows) ? rows : [], truncated, enabled: true });
}
export function clearTraceReviewReportsCache() {
    CACHE.clear();
}

// -- shaping ----------------------------------------------------------------

const toneRank = (t) => (t === "danger" ? 2 : t === "warn" ? 1 : 0);

// The report's tone on the axis: calm when it raised nothing, else the worse
// of its two worst rungs -- the same rule a finding uses, so a chapter and the
// findings inside it never disagree about how loud to be.
export function reportTone(row) {
    if (!row || row.clean || !(row.findingCount > 0)) return "calm";
    const b = row.worstBehaviour ? behaviourTone(row.worstBehaviour) : "calm";
    const c = row.worstContent ? contentTone(row.worstContent) : "calm";
    return toneRank(b) >= toneRank(c) ? b : c;
}

// Rows -> the timeline the strip and dock read: reports in WINDOW order, each
// placed on the axis by the turns its target messages land on.
//
// Window order, not completion order: the pipeline reviews windows as they
// close but drains its backlog in bursts, so completion order can put the
// deploy before the merge it followed. Placed reports sort by first turn;
// reports the loaded turns cannot place follow, by completion, so the story
// reads top to bottom and the unplaced tail is visibly a tail.
export function buildReportsTimeline(rows = [], turns = [], { truncated = false } = {}) {
    const at = new Map();
    (turns || []).forEach((t, i) => {
        const id = str(t?.messageId).toLowerCase();
        if (!id) return;
        const list = at.get(id);
        if (list) list.push(i);
        else at.set(id, [i]);
    });
    const shaped = (rows || []).map((row) => {
        const hits = (row.targetMessageIds || []).flatMap((id) => at.get(id) || []);
        const placed = hits.length > 0;
        return {
            ...row,
            tone: reportTone(row),
            placed,
            from: placed ? Math.min(...hits) : null,
            through: placed ? Math.max(...hits) : null,
            // How many of the window's messages the loaded turns carry, so a
            // window placed by two of its ninety targets is not read as tight.
            placedTargets: (row.targetMessageIds || []).filter((id) => at.has(id)).length,
        };
    });
    const byWindow = (a, b) => {
        if (a.placed !== b.placed) return a.placed ? -1 : 1;
        if (a.placed && a.from !== b.from) return a.from - b.from;
        if (a.placed && a.through !== b.through) return a.through - b.through;
        return (a.completedAt ?? 0) - (b.completedAt ?? 0);
    };
    const reports = shaped.sort(byWindow).map((r, i) => ({ ...r, index: i + 1 }));
    let completedAt = null;
    for (const r of reports) if (r.completedAt != null && (completedAt == null || r.completedAt > completedAt)) completedAt = r.completedAt;
    return {
        reports,
        placed: reports.filter((r) => r.placed).length,
        unplaced: reports.filter((r) => !r.placed).length,
        truncated: !!truncated,
        completedAt,
        source: "pipeline",
    };
}

const severityOrder = ["low", "medium", "high", "critical"];
const sensitivityOrder = ["internal", "confidential", "restricted"];
const worstOf = (values, order) => values.reduce((w, v) => (order.indexOf(v) > order.indexOf(w) ? v : w), null);

// The strip's one-line summary: how many chapters, how many raised something
// and how loud, and what only this read can be short of -- windows the loaded
// turns could not place, and windows past the read ceiling.
export function reportsSummaryLine(timeline) {
    if (!timeline?.reports?.length) return "";
    const n = timeline.reports.length;
    const flagged = timeline.reports.filter((r) => r.tone !== "calm" || (!r.clean && r.findingCount > 0));
    const parts = [`${n} report${n === 1 ? "" : "s"}`];
    if (!flagged.length) parts.push(n === 1 ? "clean" : "all clean");
    else {
        const worst = [
            worstOf(flagged.map((r) => r.worstBehaviour).filter(Boolean), severityOrder),
            worstOf(flagged.map((r) => r.worstContent).filter(Boolean), sensitivityOrder),
        ].filter(Boolean).join(" · ");
        parts.push(`${flagged.length} with findings${worst ? ` · ${worst}` : ""}`);
    }
    if (timeline.unplaced) parts.push(`${timeline.unplaced} not placed`);
    if (timeline.truncated) parts.push(`newest ${REPORTS_LIMIT} shown`);
    return parts.join(" · ");
}

// The placed reports as the scan strip's chapter segments, 0-based on the axis
// and clipped to it. The label is the summary, which is what the hover shows.
export function reportSegments(timeline, turnCount) {
    const out = [];
    for (const r of timeline?.reports || []) {
        if (!r.placed) continue;
        const from = Math.max(0, r.from);
        const through = Math.min(turnCount - 1, r.through);
        if (!(through >= from)) continue;
        out.push({ from, through, tone: r.tone, index: r.index, id: r.id, label: reportLabel(r) });
    }
    return out;
}

// What a report SAYS, or why it cannot: an aged-out document has no summary
// left to show, and the judge may legitimately leave the field empty.
export function reportLabel(r) {
    if (!r) return "";
    if (r.summary) return r.summary;
    if (r.expired) return "report document expired";
    return r.clean ? "clean, no summary written" : "no summary written";
}

// Reports whose window holds `turn`, in window order, for the dock's answer to
// "which chapter is this turn in".
export function reportsAt(timeline, turn) {
    return (timeline?.reports || []).filter((r) => r.placed && r.from <= turn && turn <= r.through);
}
